import { Select } from "antd";
import { User } from "lucide-react";
import { FC, useState } from "react";
import { TStaff } from "~/types/staff";
import { useGetAllStaff } from "~/services/operation";

type Props = {
  value?: string[];
  onChange?: (value: string[]) => void;
  staffs?: TStaff[];
  initialAssignees?: { id: string; user: { username: string; firstName?: string; lastName?: string } }[];
};

export const AssigneeSelect: FC<Props> = ({
  value,
  onChange,
  staffs: preFetchedStaffs,
  initialAssignees = [],
}) => {
  const [search, setSearch] = useState<string>("");

  const { data: fetchedStaffs, isLoading } = useGetAllStaff({
    search,
  });

  const staffList = preFetchedStaffs || fetchedStaffs?.items || [];

  const sortedTopStaff = staffList
    .map((staff: TStaff) => ({
      ...staff,
      averageRating:
        (staff.installation +
          staff.packing +
          staff.handling +
          staff.clientRelations +
          staff.teamwork) /
        5,
    }))
    .sort((a, b) => b.averageRating - a.averageRating)
    .slice(0, 20);

  const staffMap = new Map<string, string>();

  initialAssignees.forEach((assignee) => {
    if (assignee.user) {
      const name = `${assignee.user.firstName || ''} ${assignee.user.lastName || ''}`.trim() || assignee.user.username;
      staffMap.set(assignee.id, name);
    }
  });

  staffList.forEach((staff: TStaff) => {
    if (staff.user) {
      const name = `${staff.user.firstName || ''} ${staff.user.lastName || ''}`.trim() || staff.user.username;
      staffMap.set(staff.id, name);
    }
  });

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      placeholder="Select assignees"
      showSearch
      onSearch={(value) => setSearch(value)}
      loading={isLoading}
      filterOption={false}
      style={{ width: "100%" }}
      tagRender={({ value: id, closable, onClose }) => {
        const username = staffMap.get(id) || id;
        return (
          <div className="flex items-center space-x-1 bg-blue-100 text-blue-800 rounded px-2 py-1 m-1">
            <span>{username}</span>
            {closable && (
              <span
                onClick={onClose}
                className="cursor-pointer text-blue-600 hover:text-blue-800"
              >
                ×
              </span>
            )}
          </div>
        );
      }}
      options={sortedTopStaff?.map(
        (staff: TStaff & { averageRating: number }) => {
          const name = staff.user
            ? `${staff.user.firstName || ''} ${staff.user.lastName || ''}`.trim() || staff.user.username
            : 'Unknown';
          return {
            value: staff.id,
            label: (
              <div className="flex items-center space-x-2">
                <User size={16} className="text-gray-400" />
                <span>{name}</span>
                <span className="text-xs text-gray-500">
                  (Rating: {staff.averageRating.toFixed(1)})
                </span>
              </div>
            ),
          };
        }
      )}
    />
  );
};
